import {
  DISCOVER_CACHE_ENTRY_LIMIT,
  DISCOVER_CACHE_SCHEMA_VERSION,
  discoverCacheEnvelopeSchema,
  discoverCacheEntrySchema,
  type DiscoverCacheEnvelope,
  type DiscoverCacheEntry,
} from "../../../contracts/storage/discoverCache.ts";
import type { DiscoverCacheStore } from "../../../application/ports/discoverCache.ts";

export const DISCOVER_CACHE_STORAGE_KEY = "vocably.discover-cache.v1";

function emptyEnvelope(): DiscoverCacheEnvelope {
  return { schemaVersion: DISCOVER_CACHE_SCHEMA_VERSION, entries: {} };
}

function isQuotaError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  const name = "name" in error ? String(error.name) : "";
  return name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED";
}

export class LocalStorageDiscoverCache implements DiscoverCacheStore {
  constructor(private readonly storage: Storage = window.localStorage) {}

  async get(normalizedWord: string): Promise<DiscoverCacheEntry | null> {
    return this.read().entries[normalizedWord] ?? null;
  }

  async put(normalizedWord: string, entry: DiscoverCacheEntry): Promise<void> {
    const parsed = discoverCacheEntrySchema.safeParse(entry);
    if (!normalizedWord.trim() || !parsed.success) {
      return;
    }
    const envelope = this.read();
    delete envelope.entries[normalizedWord];
    const keys = Object.keys(envelope.entries);
    if (keys.length >= DISCOVER_CACHE_ENTRY_LIMIT) {
      for (const key of keys.slice(0, keys.length - DISCOVER_CACHE_ENTRY_LIMIT + 1)) {
        delete envelope.entries[key];
      }
    }
    envelope.entries[normalizedWord] = parsed.data;
    this.write(envelope);
  }

  async clear(): Promise<void> {
    try {
      this.storage.removeItem(DISCOVER_CACHE_STORAGE_KEY);
    } catch {
      // Clearing is best-effort; a blocked store should not break Settings.
    }
  }

  private read(): DiscoverCacheEnvelope {
    try {
      const raw = this.storage.getItem(DISCOVER_CACHE_STORAGE_KEY);
      if (!raw) {
        return emptyEnvelope();
      }
      const parsed = discoverCacheEnvelopeSchema.safeParse(JSON.parse(raw));
      return parsed.success ? parsed.data : emptyEnvelope();
    } catch {
      return emptyEnvelope();
    }
  }

  private write(envelope: DiscoverCacheEnvelope): void {
    try {
      this.storage.setItem(DISCOVER_CACHE_STORAGE_KEY, JSON.stringify(envelope));
    } catch (error) {
      if (isQuotaError(error)) {
        return;
      }
    }
  }
}
